/**
 * The invoices behind the "Nicht zugeordnet" and "Teilweise / prüfen" counts.
 *
 * A count tells an operator that something needs attention; it does not tell them
 * WHAT. This list closes that gap on the overview itself, so the common case —
 * "three invoices are unmapped, which ones?" — is answered without leaving the
 * dashboard.
 *
 * It is a SAMPLE, not the set. The backend caps how many invoices it names per
 * bucket (`example_limit`), because a reconciliation response must not grow with
 * the org. Where the count exceeds what was returned, the card says so in words
 * rather than letting the shorter list read as the whole story — the counts in
 * the legend above stay the authoritative figure.
 *
 * No links: the invoice detail page does not exist yet, and a row that looks
 * clickable but goes nowhere is worse than a row that plainly does not.
 */
import React, { useState } from "react";

import { fmtMoney, mapLabel, INVOICE_STATUS_LABEL } from "../bexioFormat";

/** Rows shown before the reader asks for the rest. */
const COLLAPSED_ROWS = 3;

function ExampleRow({ example, currency }) {
  const status = example?.bexio_status;
  return (
    <li className="flex items-baseline gap-2 py-1.5 text-xs">
      <span className="shrink-0 font-medium tabular-nums text-foreground">
        {example?.document_nr || `#${example?.bexio_invoice_id}`}
      </span>
      {/* The title is the only free-text field here, so it is the one that
          yields space when the row is tight. */}
      <span className="min-w-0 flex-1 truncate text-muted-foreground">
        {example?.title || "Ohne Titel"}
      </span>
      {status ? (
        <span className="shrink-0 text-muted-foreground">
          {mapLabel(INVOICE_STATUS_LABEL, status)}
        </span>
      ) : null}
      <span className="shrink-0 whitespace-nowrap tabular-nums text-foreground">
        {fmtMoney(example?.total, currency)}
      </span>
    </li>
  );
}

function Bucket({ bucket, currency }) {
  const [expanded, setExpanded] = useState(false);
  const examples = Array.isArray(bucket.examples) ? bucket.examples : [];
  if (!bucket.count || examples.length === 0) return null;

  const shown = expanded ? examples : examples.slice(0, COLLAPSED_ROWS);
  const hiddenLocally = examples.length - shown.length;
  // The backend named fewer invoices than it counted.
  const beyondSample = bucket.count - examples.length;

  return (
    <div>
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${bucket.dot}`} />
        <h3 className="flex-1 truncate text-xs font-semibold text-foreground">
          {bucket.label}
        </h3>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {bucket.count}
        </span>
      </div>

      <ul className="mt-1 divide-y divide-border">
        {shown.map((e) => (
          <ExampleRow key={e.bexio_invoice_id} example={e} currency={currency} />
        ))}
      </ul>

      {hiddenLocally > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {hiddenLocally} weitere anzeigen
        </button>
      ) : null}
      {expanded && examples.length > COLLAPSED_ROWS ? (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Weniger anzeigen
        </button>
      ) : null}

      {beyondSample > 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Zeigt {examples.length} von {bucket.count} — die übrigen {beyondSample} stehen
          auf der Rechnungsliste.
        </p>
      ) : null}
    </div>
  );
}

export default function BexioCoverageExamples({ coverage, currency }) {
  const buckets = [
    {
      key: "unmapped",
      label: "Nicht zugeordnet",
      count: Number(coverage?.unmapped) || 0,
      examples: coverage?.unmapped_examples,
      dot: "bg-red-600 dark:bg-red-400",
    },
    {
      key: "partial",
      label: "Teilweise / prüfen",
      count: Number(coverage?.partial_maybe_invalid) || 0,
      examples: coverage?.partial_examples,
      dot: "bg-amber-500 dark:bg-amber-400",
    },
  ];

  const visible = buckets.filter(
    (b) => b.count > 0 && Array.isArray(b.examples) && b.examples.length > 0
  );
  // Nothing to name — either everything is mapped, or this response predates the
  // examples field. Either way the donut above already told the whole story.
  if (visible.length === 0) return null;

  return (
    <div className="mt-4 space-y-4 border-t border-border pt-3">
      {visible.map((b) => (
        <Bucket key={b.key} bucket={b} currency={currency} />
      ))}
    </div>
  );
}
